import { z } from 'zod';
import { evaluateExpression } from '../shared';
import { parseLaunchAngles, parseTarget } from './trajectory';

function validNumber(value: string, predicate: (number: number) => boolean): boolean {
  const number = evaluateExpression(value);
  return number !== null && Number.isFinite(number) && predicate(number);
}

export const schema = z
  .object({
    project: z.string().trim().min(1, 'Project is required'),
    title: z.string().trim().min(1, 'Title is required'),
    rho0: z.string().refine(value => validNumber(value, number => number > 0), 'Air density must be greater than zero'),
    h0: z.string().refine(value => validNumber(value, number => number >= 0), 'Launch height must be nonnegative'),
    v0: z.string().refine(value => validNumber(value, number => number > 0), 'Launch velocity must be greater than zero'),
    angles: z.string().min(1, 'Launch angles are required'),
    Wt: z.string().refine(value => validNumber(value, number => number > 0), 'Fragment weight must be greater than zero'),
    AD: z.string().refine(value => validNumber(value, number => number > 0), 'Drag area must be greater than zero'),
    CD: z.string().refine(value => validNumber(value, number => number > 0), 'Drag coefficient must be greater than zero'),
    FS: z.string().refine(value => validNumber(value, number => number > 0), 'DDESB distance factor must be greater than zero'),
    target: z.string().min(1, 'Target is required')
  })
  .superRefine((values, context) => {
    try {
      parseLaunchAngles(values.angles);
    } catch (error) {
      context.addIssue({ code: 'custom', path: ['angles'], message: error instanceof Error ? error.message : 'Invalid launch angles.' });
    }

    try {
      parseTarget(values.target);
    } catch (error) {
      context.addIssue({ code: 'custom', path: ['target'], message: error instanceof Error ? error.message : 'Invalid target.' });
    }
  });

export type FormValues = z.input<typeof schema>;
export type ParsedValues = z.output<typeof schema>;
